import {
  InteractionStatus,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { useAccount, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { startLoginRedirect } from '../auth/interactiveAuth';
import { loginRequest } from '../auth/msalConfig';

import { AuthContext } from '.';

import type { AuthState } from '@/types/authTypes';

const anonymousDetails = {
  email: null,
  name: null,
  roles: [] as [],
};

const createInitializingState = (): AuthState => ({
  ...anonymousDetails,
  initialized: false,
  authenticated: false,
  status: 'initializing',
  error: null,
});

const createUnauthenticatedState = (): AuthState => ({
  ...anonymousDetails,
  initialized: true,
  authenticated: false,
  status: 'unauthenticated',
  error: null,
});

const createAnonymousErrorState = (error: string): AuthState => ({
  ...anonymousDetails,
  initialized: true,
  authenticated: false,
  status: 'error',
  error,
});

// msal-common types idTokenClaims.roles as `string[]`, but that's an unverified assertion
// over JSON decoded from the ID token — the SDK never checks the claim's actual shape.
const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

const getRoles = (account: AccountInfo): string[] => {
  const roles = account.idTokenClaims?.roles;
  return isStringArray(roles) ? roles : [];
};

const createAuthenticatedState = (account: AccountInfo): AuthState => ({
  initialized: true,
  authenticated: true,
  status: 'authenticated',
  error: null,
  email: account.username,
  name: account.name ?? null,
  roles: getRoles(account),
});

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'An unexpected authentication error occurred.';

// Query params Azure AD echoes back after a login/logout redirect. MSAL reads and validates
// these from the URL itself; nothing in MSAL strips them from the address bar afterward.
const AUTH_RESPONSE_PARAMS = [
  'code',
  'state',
  'session_state',
  'client_info',
  'error',
  'error_description',
];

const stripAuthResponseParams = (): void => {
  const url = new URL(window.location.href);
  if (!AUTH_RESPONSE_PARAMS.some(param => url.searchParams.has(param))) return;

  AUTH_RESPONSE_PARAMS.forEach(param => {
    url.searchParams.delete(param);
  });
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

/**
 * Resolves the application's authentication state for the active MSAL account.
 *
 * `MsalProvider` handles the MSAL authentication lifecycle, including processing
 * redirect responses and synchronizing account information. This function should
 * be called after `inProgress` reaches `InteractionStatus.None`, when those
 * operations have completed.
 *
 * If an active account exists, the function verifies that MSAL can silently acquire
 * an access token, then builds an authenticated state from the account's profile and
 * token claims. If token acquisition fails, the provider clears the authenticated state
 * so protected routes can start a fresh login.
 *
 * @param instance - The MSAL client instance used to acquire the access token.
 * @param account - The active MSAL account, or `null` when no user is signed in.
 * @returns The resolved application authentication state.
 */
const resolveAuthState = async (
  instance: IPublicClientApplication,
  account: AccountInfo | null
): Promise<AuthState> => {
  if (!account) {
    return createUnauthenticatedState();
  }

  await instance.acquireTokenSilent({ ...loginRequest, account });
  return createAuthenticatedState(account);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { instance, inProgress, accounts } = useMsal();
  const activeAccount = useAccount();
  const cachedActiveAccount = instance.getActiveAccount();
  // A single cached account is unambiguous. With multiple cached accounts, require MSAL's
  // active-account pointer instead of silently selecting an arbitrary identity.
  const account =
    activeAccount ?? cachedActiveAccount ?? (accounts.length === 1 ? (accounts[0] ?? null) : null);
  const hasAmbiguousAccounts = !activeAccount && !cachedActiveAccount && accounts.length > 1;
  const [state, setState] = useState<AuthState>(createInitializingState);
  const latestRefreshRef = useRef(0);
  const resolvedAccountIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      latestRefreshRef.current += 1;
    };
  }, []);

  const refresh = useCallback(async () => {
    const refreshId = ++latestRefreshRef.current;
    const accountId = account?.homeAccountId ?? null;

    // Do not expose the previous account's identity or roles while a new account is resolving.
    if (resolvedAccountIdRef.current !== accountId) {
      setState(createInitializingState());
    }

    try {
      const next = await resolveAuthState(instance, account);
      if (!mountedRef.current || refreshId !== latestRefreshRef.current) return;

      resolvedAccountIdRef.current = accountId;
      setState(next);
    } catch (error: unknown) {
      if (!mountedRef.current || refreshId !== latestRefreshRef.current) return;

      resolvedAccountIdRef.current = accountId;

      // A failed silent acquisition invalidates the application's authenticated state.
      // Protected routes will start a new login so the user can establish a fresh session.
      console.error('[auth] Failed to acquire an access token silently.', error);
      try {
        await instance.clearCache();
      } catch (clearCacheError: unknown) {
        console.error('[auth] Failed to clear the local MSAL cache.', clearCacheError);
      }
      instance.setActiveAccount(null);
      setState(createUnauthenticatedState());
    }
  }, [instance, account]);

  useEffect(() => {
    // Wait for MSAL's own startup/redirect handling to settle before resolving state
    if (inProgress !== InteractionStatus.None) return;
    stripAuthResponseParams();
    if (hasAmbiguousAccounts) {
      latestRefreshRef.current += 1;
      setState(
        createAnonymousErrorState(
          'Multiple cached accounts are available, but no active account has been selected.'
        )
      );
      return;
    }
    if (account && !activeAccount) {
      instance.setActiveAccount(account);
    }
    void refresh();
  }, [inProgress, hasAmbiguousAccounts, account, activeAccount, instance, refresh]);

  const login = useCallback(() => {
    if (inProgress !== InteractionStatus.None) return;

    setState(createInitializingState());
    const request = hasAmbiguousAccounts
      ? { ...loginRequest, prompt: 'select_account' as const }
      : loginRequest;

    void startLoginRedirect(instance, request).catch((error: unknown) => {
      console.error('[auth] Failed to start the login redirect.', error);
      setState(createAnonymousErrorState(getErrorMessage(error)));
    });
  }, [hasAmbiguousAccounts, inProgress, instance]);

  const logout = useCallback(() => {
    if (inProgress !== InteractionStatus.None) return;

    latestRefreshRef.current += 1;
    resolvedAccountIdRef.current = null;
    setState(createUnauthenticatedState());

    void instance.logoutRedirect({ postLogoutRedirectUri: '/' }).catch((error: unknown) => {
      console.error('[auth] Failed to start the logout redirect.', error);
      setState(createAnonymousErrorState(getErrorMessage(error)));
    });
  }, [inProgress, instance]);

  const interactionInProgress = inProgress !== InteractionStatus.None;

  const contextValue = useMemo(
    () => ({ ...state, interactionInProgress, login, logout, refresh }),
    [state, interactionInProgress, login, logout, refresh]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
